import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Divider,
  Group,
  List,
  Modal,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconBuilding, IconChevronRight } from '@tabler/icons-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { seja, type Instanca } from '../seja/seja';
import { sporocilo } from '../api/napaka';
import { IME_APLIKACIJE } from '../ime';
import { TemnoOzadje } from '../postavitev/TemnoOzadje';
import { barve } from '../tema';

/**
 * Prijava z uporabniškim imenom in geslom; pri več podjetjih nato izbira podjetja (enotna prijava).
 * Povezavi na pozabljeno geslo in registracijo (brezplačni preizkus).
 * Kljukica »Ostani prijavljen« ob kliku razloži, kako dolgo prijava velja (trajanja določi strežnik, hera.varnost.*).
 */
export function Prijava() {
  const [ime, setIme] = useState('');
  const [geslo, setGeslo] = useState('');
  const [ostani, setOstani] = useState(false);
  const [pojasnilo, setPojasnilo] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [posiljam, setPosiljam] = useState(false);
  const [izbira, setIzbira] = useState<Instanca[] | null>(null);

  const prijavi = async (domena?: string) => {
    setNapaka(null);
    setPosiljam(true);
    try {
      const r = await seja.prijava(ime, geslo, domena, ostani);
      if ('izbiraInstance' in r) {
        setIzbira(r.izbiraInstance);
      }
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setPosiljam(false);
    }
  };

  const oddaj = (e: FormEvent) => {
    e.preventDefault();
    void prijavi();
  };

  return (
    <TemnoOzadje>
      <Paper radius="lg" p={36} shadow="xl">
        {izbira ? (
          <Stack gap="xs">
            <Title order={2} size="h3">
              Izberite podjetje
            </Title>
            <Text size="sm" c="dimmed" mb="sm">
              Uporabnik {ime} ima dostop do več podjetij.
            </Text>
            {izbira.map((i) => (
              <UnstyledButton
                key={i.domena}
                onClick={() => void prijavi(i.domena)}
                disabled={posiljam}
                p="sm"
                style={{ border: `1px solid ${barve.obroba}`, borderRadius: 10 }}
              >
                <Text fw={600} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <IconBuilding size={18} color={barve.primarna} />
                  <span style={{ flex: 1 }}>{i.naziv}</span>
                  <IconChevronRight size={16} color={barve.besediloSekundarno} />
                </Text>
              </UnstyledButton>
            ))}
            {napaka && <Napaka besedilo={napaka} />}
            <Button variant="subtle" mt="sm" onClick={() => setIzbira(null)}>
              Nazaj
            </Button>
          </Stack>
        ) : (
          <form onSubmit={oddaj} noValidate>
            <Stack gap="md">
              <div>
                <Title order={2} size="h3">
                  Prijava
                </Title>
                <Text size="sm" c="dimmed">
                  Vnesite svoje uporabniške podatke.
                </Text>
              </div>
              <TextInput
                label="Uporabniško ime"
                value={ime}
                onChange={(e) => setIme(e.currentTarget.value)}
                autoComplete="username"
                autoFocus
              />
              <div>
                <PasswordInput
                  label="Geslo"
                  value={geslo}
                  onChange={(e) => setGeslo(e.currentTarget.value)}
                  autoComplete="current-password"
                />
                <Text size="sm" ta="right" mt={6}>
                  <Anchor component={Link} to="/pozabljeno-geslo">
                    Pozabljeno geslo?
                  </Anchor>
                </Text>
              </div>
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Checkbox
                  label="Ostani prijavljen"
                  checked={ostani}
                  onChange={(e) => {
                    const da = e.currentTarget.checked;
                    setOstani(da);
                    if (da) {
                      setPojasnilo(true);
                    }
                  }}
                />
                <Anchor component="button" type="button" size="sm" onClick={() => setPojasnilo(true)}>
                  Kaj to pomeni?
                </Anchor>
              </Group>
              {napaka && <Napaka besedilo={napaka} />}
              <Button type="submit" fullWidth mt="sm" loading={posiljam}>
                Prijava
              </Button>
              <Divider label="Nimate računa?" labelPosition="center" />
              <Button component={Link} to="/registracija" variant="light" fullWidth>
                Brezplačni preizkus
              </Button>
            </Stack>
          </form>
        )}
      </Paper>
      <PojasniloOstani
        odprto={pojasnilo}
        potrdi={() => {
          setOstani(true);
          setPojasnilo(false);
        }}
        zavrni={() => {
          setOstani(false);
          setPojasnilo(false);
        }}
      />
    </TemnoOzadje>
  );
}

/**
 * Kratko pojasnilo za »Ostani prijavljen« za netehnične uporabnike. Časi so isti kot na strežniku:
 * hera.varnost.osvezilni-zeton (30 dni), najdaljsa-prijava (90 dni), osvezilni-zeton-kratek (12 ur).
 */
function PojasniloOstani({ odprto, potrdi, zavrni }: { odprto: boolean; potrdi: () => void; zavrni: () => void }) {
  return (
    <Modal opened={odprto} onClose={zavrni} title="Ostani prijavljen" radius="lg" centered>
      <Stack gap="sm">
        <Text size="sm">
          {IME_APLIKACIJE} si zapomni vašo prijavo <b>na tej napravi in v tem brskalniku</b>. Ko jo naslednjič odprete,
          vam gesla ne bo treba vpisati znova.
        </Text>
        <List size="sm" spacing={6}>
          <List.Item>Prijavljeni ostanete, tudi ko zaprete brskalnik ali ugasnete računalnik.</List.Item>
          <List.Item>
            Če aplikacije <b>30 dni</b> ne odprete, vas odjavi. Vsaka uporaba ta rok spet podaljša na 30 dni.
          </List.Item>
          <List.Item>
            Najkasneje po <b>90 dneh</b> vas aplikacija vseeno prosi za geslo, tudi če jo uporabljate vsak dan.
          </List.Item>
          <List.Item>Odjavite se lahko kadar koli: kliknite svoje ime zgoraj desno in izberite Odjava.</List.Item>
        </List>
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={18} />} p="sm">
          <Text size="sm">
            Ne označite na računalniku, ki ga uporablja več ljudi (skupni ali javni računalnik): kdor ga uporablja za
            vami, ima brez gesla dostop do vaših računov in strank.
          </Text>
        </Alert>
        <Text size="sm" c="dimmed">
          Brez kljukice ostanete prijavljeni, dokler je brskalnik odprt. Ko ga zaprete ali Here 12 ur nimate odprte, se
          boste morali prijaviti znova.
        </Text>
        <Group justify="flex-end" gap="xs" mt="xs">
          <Button variant="subtle" onClick={zavrni}>
            Ne, hvala
          </Button>
          <Button onClick={potrdi}>Ostani prijavljen</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function Napaka({ besedilo }: { besedilo: string }) {
  return (
    <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} role="alert">
      {besedilo}
    </Alert>
  );
}
